import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useAuthToken } from "@/contexts/OptimizedAuthContext";
import { useSearchParams, useParams } from "@/hooks/useRouter";
import { useFind } from "@/hooks/useFind";
import { useFilters } from "@/hooks/useFilters";
import {
  // searchMarkdown,
  searchMarkdownGraphQL as searchMarkdown,
  ensureAuthenticated,
  fetchProjects,
  fetchSessionDetail,
  fetchRecentSessions,
  type SessionDetail,
} from "@/services/api";
import { splitExchangeParts } from "@/lib/utils/exchange";
import { TIME_FILTERS, type TimeFilter } from "../../../shared/utils/dateRange";
import { escapedQueryRegex } from "@/lib/utils/exchange";

// Re-export the SearchResult type from the API service
export type { SearchResult, Exchange } from "@/services/api";

const DEFAULT_SEARCH_LIMIT = 200;

export function useSearch() {
  const searchParams = useSearchParams();
  const getToken = useAuthToken();
  const { initializeFindQuery } = useFind();
  const { projectIds, timeFilter, agentNames } = useFilters();
  const query = searchParams.get("q") || "";
  const pageParam = searchParams.get("page");
  const limitParam = searchParams.get("limit");
  let page = parseInt(pageParam || "1");
  if (isNaN(page)) {
    page = 1;
  }
  let limit = parseInt(limitParam || DEFAULT_SEARCH_LIMIT.toString());
  if (isNaN(limit)) {
    limit = DEFAULT_SEARCH_LIMIT;
  }

  // Validate that timeFilter is a valid TimeFilter value
  const validatedTimeFilter: TimeFilter | undefined =
    timeFilter && TIME_FILTERS.includes(timeFilter as TimeFilter)
      ? (timeFilter as TimeFilter)
      : undefined;

  return useQuery({
    queryKey: [
      "search",
      query,
      page,
      validatedTimeFilter,
      ...projectIds,
      ...agentNames,
    ],
    queryFn: async () => {
      // Initialize find query with search query
      initializeFindQuery(query);

      // Ensure we have a valid authentication token before making the API call
      await ensureAuthenticated(getToken);
      const result = await searchMarkdown(
        query,
        projectIds,
        validatedTimeFilter,
        agentNames,
        limit,
        page
      );
      const results = result.results.map((r) => {
        const exchanges = r.matchingExchanges.map((e) => {
          const parts = splitExchangeParts(e.content, query);
          const matches = e.content.match(escapedQueryRegex(query, true));
          return {
            ...e,
            splitExchanges: parts,
            matchesCount: matches?.length || 0,
          };
        });
        const totalMatches = exchanges.reduce(
          (acc, e) => acc + (e.matchesCount || 0),
          0
        );
        return {
          ...r,
          matchingExchanges: exchanges,
          totalMatches,
        };
      });
      return {
        ...result,
        results,
      };
    },
    enabled: !!query, // Only run the query if there's a search term
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useProjects() {
  const getToken = useAuthToken();

  return useQuery({
    queryKey: ["projects"],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      // Ensure we have a valid authentication token before making the API call
      await ensureAuthenticated(getToken);
      const projects = await fetchProjects();
      // Icon and color now come from the database
      return projects;
    },
  });
}

// Single source of truth for the session-detail query (key + fetch + transform)
// so the list (prefetch on hover) and the detail view share the exact same key
// and result — a hover warms precisely the cache the click reads.
function sessionQueryOptions(
  projectId: string | undefined,
  sessionId: string | undefined,
  getToken: ReturnType<typeof useAuthToken>
) {
  return {
    queryKey: ["session", sessionId] as const,
    queryFn: async () => {
      if (!projectId || !sessionId) return null;

      await ensureAuthenticated(getToken);
      const session = await fetchSessionDetail(projectId, sessionId);

      return {
        ...session,
        exchanges: session?.exchanges
          ?.map((e) => ({
            ...e,
            splitExchanges: splitExchangeParts(e.content) || [],
          }))
          .sort((a, b) => a.orderNumber - b.orderNumber),
      };
    },
  };
}

export function useSession(
  explicitProjectId?: string,
  explicitSessionId?: string
) {
  const getToken = useAuthToken();
  const paramsFromUrl = useParams();

  // Use explicit params if provided, otherwise fall back to URL params
  const projectId = explicitProjectId ?? paramsFromUrl.projectId;
  const sessionId = explicitSessionId ?? paramsFromUrl.sessionId;

  return useQuery({
    ...sessionQueryOptions(projectId, sessionId, getToken),
    enabled: !!projectId && !!sessionId,
  });
}

// Warm the session-detail cache (e.g. on row hover/focus) so the click is
// instant. No-ops if already cached/fresh; expires per gcTime if never clicked.
export function usePrefetchSession() {
  const getToken = useAuthToken();
  const queryClient = useQueryClient();

  return useCallback(
    (projectId: string, sessionId: string) => {
      if (!projectId || !sessionId) return;
      queryClient.prefetchQuery(
        sessionQueryOptions(projectId, sessionId, getToken)
      );
    },
    [getToken, queryClient]
  );
}

export function useRecentSessions(limit: number = 5) {
  const getToken = useAuthToken();

  return useQuery({
    queryKey: ["recentSessions", limit],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      await ensureAuthenticated(getToken);
      const recentSessions = await fetchRecentSessions(limit);

      // Map the backend response to the UI format
      // Now uses exchangeCount and firstExchangeContent from the API instead of processing all exchanges
      return recentSessions.map((item: SessionDetail) => {
        // Extract first prompt preview from firstExchangeContent if available
        let firstPromptPreview = "";

        if (item.firstExchangeContent) {
          try {
            // Use the optimized function that works directly with content strings
            const parsedExchange = splitExchangeParts(
              item.firstExchangeContent
            );
            firstPromptPreview = parsedExchange?.userMessagePreview || "";
          } catch (error) {
            // If extraction fails, log a warning but continue processing other sessions
            // In development, log full error details for debugging
            if (process.env.NODE_ENV === "development") {
              console.error(
                `Failed to extract first prompt preview from session ${item.id}:`,
                error
              );
            } else {
              console.warn(
                `Failed to extract first prompt preview from session ${item.id}:`,
                error instanceof Error ? error.message : String(error)
              );
            }
            // TODO: Consider sending to error tracking service in production
          }
        }

        return {
          session: {
            ...item,
            // Note: exchanges array is empty when using exchangeCount optimization
            // Use exchangeCount for accurate count, not exchanges.length
            // This is for backward compatibility with code that expects an exchanges property
            exchanges: item.exchanges || [],
          },
          projectName: item.project?.name || "",
          projectId: item.projectId, // Use the session's projectId, not workspace.id
          projectIcon: item.project?.icon || "",
          projectColor: item.project?.color || "",
          firstPromptPreview, // Add the pre-computed preview
        };
      });
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}
